import { Fragment, type ReactNode } from 'react'

import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import type { SxProps } from '@mui/material/styles'

import { Text } from '@mxenabled/mxui'

import InsightTransactionRow, { type InsightTransactionRowProps } from './InsightTransactionRow'

export interface FooterRow {
  label: ReactNode
  amount: ReactNode
  amountColor?: string
}

type TransactionListCardVariant = 'default' | 'primary'

interface TransactionListCardProps {
  /** Optional header label above the rows (e.g. "{Date}", "3 transactions"). */
  header?: ReactNode
  /** One or more transaction rows. */
  rows: InsightTransactionRowProps[]
  /** Optional summary/total row rendered beneath a divider. */
  footer?: FooterRow
  /**
   * Card style:
   * - `default` — grey utility header, hairline border, radius 4.
   * - `primary` — filled `primary.main` header with white text and a matching
   *   border, radius 8 (Figma "Multi" variant).
   *
   * In both cases the header is borderless; the border wraps the content block
   * below it.
   */
  variant?: TransactionListCardVariant
  /**
   * Optional background applied to the Slot behind the card. Must be a valid CSS
   * background string (gradient or color in CSS format); MUI theme tokens are
   * not resolved here (this is the CSS `background` shorthand, not `bgcolor`), so
   * use a CSS color literal instead. When set, the inner card renders borderless
   * on a white surface inside the padded Slot (Figma HSA Deposit).
   */
  background?: string
  sx?: SxProps
}

const getVariantStyles = (variant: TransactionListCardVariant) => {
  switch (variant) {
    case 'primary':
      // Borderless filled header on top of the bordered rows block.
      return {
        radius: '8px',
        borderColor: 'primary.main',
        headerBg: 'primary.main',
        headerColor: 'primary.contrastText',
        headerBold: false,
        headerInsideBorder: false,
        headerSx: { minHeight: 24, px: 12, py: 4 } as const,
      }
    default:
      // A single border wraps the card; the grey header sits inside it.
      return {
        radius: '4px',
        borderColor: 'divider',
        headerBg: 'background.default',
        headerColor: 'text.secondary',
        headerBold: true,
        headerInsideBorder: true,
        headerSx: { minHeight: 40, px: 16, py: 2 } as const,
      }
  }
}

/**
 * List card composing an optional header label, one or more
 * `InsightTransactionRow`s (divided), and an optional footer/total row.
 * `variant` supplies the Figma design defaults for the two card styles.
 *
 * Scope is the Content section only; Header/Description are out of scope.
 */
const TransactionListCard = ({
  background,
  footer,
  header,
  rows,
  variant = 'default',
  sx,
}: TransactionListCardProps) => {
  // rows usually comes from mapped API data, so an empty array is reachable and
  // would render a broken border shell; render nothing instead.
  if (rows.length === 0) return null

  const styles = getVariantStyles(variant)
  const hasBackground = Boolean(background)
  const radius = hasBackground ? '8px' : styles.radius
  // The header sits inside the border only for the bordered `default` variant;
  // for `primary`/backgrounded cards it sits above a separately-bordered body.
  const headerInsideBorder = Boolean(header) && !hasBackground && styles.headerInsideBorder

  const headerBlock = header ? (
    <Stack sx={{ bgcolor: styles.headerBg, justifyContent: 'center', ...styles.headerSx }}>
      <Text bold={styles.headerBold} color={styles.headerColor} variant="caption">
        {header}
      </Text>
    </Stack>
  ) : null

  const footerBlock = footer ? (
    <>
      <Divider />
      <Stack
        sx={{
          alignItems: 'center',
          bgcolor: hasBackground ? 'background.paper' : 'background.default',
          flexDirection: 'row',
          justifyContent: 'space-between',
          minHeight: 40,
          px: 16,
          py: 2,
        }}
      >
        <Text color="text.secondary" variant="caption">
          {footer.label}
        </Text>
        <Text bold={true} color={footer.amountColor ?? 'text.primary'} variant="body2">
          {footer.amount}
        </Text>
      </Stack>
    </>
  ) : null

  const rowsBlock = (
    <Stack role="list" sx={{ width: '100%' }}>
      {rows.map((row, index) => (
        // Static, caller-ordered list that may contain identical rows, so the
        // array index is the correct key (a content-based key would collide).
        // eslint-disable-next-line react/no-array-index-key
        <Fragment key={index}>
          {index > 0 && <Divider role="presentation" />}
          <Box role="listitem">
            <InsightTransactionRow {...row} />
          </Box>
        </Fragment>
      ))}
    </Stack>
  )

  return (
    <Stack
      sx={{
        ...(hasBackground ? { background, p: 16 } : { pt: 8, px: 16 }),
        width: '100%',
        ...sx,
      }}
    >
      {/* Header above the border for `primary`/backgrounded cards; top-rounded. */}
      {!headerInsideBorder && headerBlock && (
        <Stack
          sx={{
            borderTopLeftRadius: radius,
            borderTopRightRadius: radius,
            overflow: 'hidden',
          }}
        >
          {headerBlock}
        </Stack>
      )}

      <Stack
        sx={{
          ...(hasBackground
            ? { bgcolor: 'background.paper' }
            : { border: '1px solid', borderColor: styles.borderColor }),
          borderBottomLeftRadius: radius,
          borderBottomRightRadius: radius,
          borderTopLeftRadius: headerInsideBorder || !header ? radius : 0,
          borderTopRightRadius: headerInsideBorder || !header ? radius : 0,
          overflow: 'hidden',
          width: '100%',
        }}
      >
        {/* Header inside the border for the `default` table variant. */}
        {headerInsideBorder && (
          <>
            {headerBlock}
            <Divider />
          </>
        )}
        {rowsBlock}
        {footerBlock}
      </Stack>
    </Stack>
  )
}

export default TransactionListCard
